/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IButtonProps, IButtonStyles, IconButton } from '@fluentui/react/lib/Button';
import { ISearchBoxProps, ISearchBoxStyles } from '@fluentui/react/lib/SearchBox';
import { IStyle } from '@fluentui/react/lib/Styling';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisDebouncedSearchBox } from 'src/core/components/LuisDebouncedSearchBox';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';

const messages = defineMessages({
    namePlaceholder: {
        id: 'luis.intentLabelingSidePane.entityTreeFilterBox.namePlaceholder',
        defaultMessage: 'Filter by name'
    },
    recentPlaceholder: {
        id: 'luis.intentLabelingSidePane.entityTreeFilterBox.recentPlaceholder',
        defaultMessage: 'Recently used'
    },
    nameTooltip: {
        id: 'luis.intentLabelingSidePane.entityTreeFilterBox.nameTooltip',
        defaultMessage: 'Filter by name'
    },
    recentTooltip: {
        id: 'luis.intentLabelingSidePane.entityTreeFilterBox.recentTooltip',
        defaultMessage: 'Filter by recently used'
    },
    closeTooltip: {
        id: 'luis.intentLabelingSidePane.entityTreeFilterBox.closeTooltip',
        defaultMessage: 'Close filter'
    },
    filterType: {
        id: 'luis.intentLabelingSidePane.entityTreeFilterBox.filterType',
        defaultMessage: 'Filter type'
    }
});

const { styleDiv } = getStylistV2('EntityTreeFilterBox');
const scopedTheme = getScopedTheme('EntityTreeFilterBox');

const Root = styleDiv('Root', {
    position: 'relative'
});

const commonIconButtonStyles: IStyle = {
    position: 'absolute',
    bottom: 2
};

const iconStyles: IStyle = { color: scopedTheme.iconColor };

const filterTypeIconButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    root: {
        ...commonIconButtonStyles,
        right: 32
    },
    icon: iconStyles,
    menuIcon: { display: 'none' }
});

// To account for the buttons on the searchbox, padding needs to be added.
const inputPadding = 72;
const searchBoxStyles = classNamesFunction<ISearchBoxProps, ISearchBoxStyles>()({
    field: { paddingRight: inputPadding, width: `calc(100% - ${inputPadding}px)` },
    icon: { display: 'none' },
    iconContainer: { display: 'none' },
    clearButton: { display: 'none' }
});

const dismissIconButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    root: {
        ...commonIconButtonStyles,
        right: 0
    },
    icon: { ...iconStyles, fontSize: 12 }
});

export type EntityTreeFilterBoxProps = {
    /**
     * Query text.
     */
    query: string;
    /**
     * Callback to update the query.
     */
    onChangeQuery: (query: string) => void;
    /**
     * Callback to dismiss the search box.
     */
    onDismiss: () => void;
};

export const EntityTreeFilterBox = (props: EntityTreeFilterBoxProps) => {
    const { query, onChangeQuery, onDismiss } = props;
    const { localize } = useLocalization();

    const latestQueryRef = React.useRef('');

    const filterButtonIdRef = React.useRef(getId('EntityTreeFilterBox'));
    const dismissButtonIdRef = React.useRef(getId('EntityTreeFilterBox'));

    const [filterType, setFilterType] = React.useState<'name' | 'recent'>('name');
    const placeholder = localize(messages[filterType === 'name' ? 'namePlaceholder' : 'recentPlaceholder']);

    const onKeyUp = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === LuisKeyCodes.Escape) {
            onDismiss();
        }
    };

    const changeFilterType = (newFilterType: 'name' | 'recent') => {
        if (newFilterType === 'recent') {
            latestQueryRef.current = query;
            onChangeQuery('');
        } else {
            onChangeQuery(latestQueryRef.current);
        }

        setFilterType(newFilterType);
    };

    return (
        <Root>
            <LuisDebouncedSearchBox
                styles={searchBoxStyles}
                disabled={filterType === 'recent'}
                autoFocus
                onClear={onDismiss}
                underlined
                iconProps={{ style: { display: 'none' } }}
                onChange={(_, q) => onChangeQuery(q)}
                ariaLabel={placeholder}
                placeholder={placeholder}
                onKeyUp={onKeyUp}
                value={query}
            />
            <LuisTooltip content={localize(messages.filterType)} calloutProps={{ target: `#${filterButtonIdRef.current}` }}>
                <IconButton
                    id={filterButtonIdRef.current}
                    styles={filterTypeIconButtonStyles}
                    iconProps={{ iconName: filterType === 'name' ? 'TextField' : 'Recent' }}
                    menuProps={{
                        items: [
                            {
                                key: 'name',
                                iconProps: { iconName: 'TextField' },
                                text: localize(messages.nameTooltip),
                                onClick: () => changeFilterType('name')
                            },
                            {
                                key: 'recent',
                                iconProps: { iconName: 'Recent' },
                                text: localize(messages.recentTooltip),
                                onClick: () => changeFilterType('recent')
                            }
                        ]
                    }}
                />
            </LuisTooltip>
            <LuisTooltip content={localize(messages.closeTooltip)} calloutProps={{ target: `#${dismissButtonIdRef.current}` }}>
                <IconButton
                    id={dismissButtonIdRef.current}
                    styles={dismissIconButtonStyles}
                    onClick={onDismiss}
                    iconProps={{ iconName: 'Clear' }}
                />
            </LuisTooltip>
        </Root>
    );
};
